import { IngredientGroups } from '@cookspace-shared-contracts/core';

import { baseApi } from '@/api/baseApi';
import { parseWithSchema } from '@/lib/safeParse';

/**
 * RTK Query endpoints для admin-backend /ingredient-groups/*.
 *
 * Глобальный справочник групп ингредиентов (платформенные, без cafeId).
 * Все ответы парсятся через Zod, чтобы поймать drift контрактов.
 * DELETE отдаёт HTTP 204 без тела — для него парсинг body пропускаем
 * (api-contracts.md): EmptyResponseSchema не принимает null от 204.
 */
export const ingredientGroupsApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        listIngredientGroups: build.query<
            IngredientGroups.GetIngredientGroupsResponse,
            IngredientGroups.GetIngredientGroupsRequest | void
        >({
            query: (params) => {
                const search: Record<string, string> = {};
                if (params?.isActive !== undefined) {
                    search.isActive = params.isActive ? 'true' : 'false';
                }
                return {
                    url: '/ingredient-groups',
                    method: 'GET',
                    params: search
                };
            },
            transformResponse: (raw: unknown): IngredientGroups.GetIngredientGroupsResponse =>
                parseWithSchema(IngredientGroups.GetIngredientGroupsResponseSchema, raw),
            providesTags: (result) =>
                result
                    ? [
                        ...result.items.map((g) => ({
                            type: 'IngredientGroup' as const,
                            id: g.id
                        })),
                        { type: 'IngredientGroup' as const, id: 'LIST' }
                    ]
                    : [{ type: 'IngredientGroup' as const, id: 'LIST' }]
        }),

        createIngredientGroup: build.mutation<
            IngredientGroups.CreateIngredientGroupResponse,
            IngredientGroups.CreateIngredientGroupRequest
        >({
            query: (body) => ({
                url: '/ingredient-groups',
                method: 'POST',
                body
            }),
            transformResponse: (raw: unknown): IngredientGroups.CreateIngredientGroupResponse =>
                parseWithSchema(IngredientGroups.CreateIngredientGroupResponseSchema, raw),
            invalidatesTags: [{ type: 'IngredientGroup', id: 'LIST' }]
        }),

        updateIngredientGroup: build.mutation<
            IngredientGroups.UpdateIngredientGroupResponse,
            { id: string; body: IngredientGroups.UpdateIngredientGroupRequest }
        >({
            query: ({ id, body }) => ({
                url: `/ingredient-groups/${id}`,
                method: 'PATCH',
                body
            }),
            transformResponse: (raw: unknown): IngredientGroups.UpdateIngredientGroupResponse =>
                parseWithSchema(IngredientGroups.UpdateIngredientGroupResponseSchema, raw),
            invalidatesTags: (_result, _error, arg) => [
                { type: 'IngredientGroup', id: arg.id },
                { type: 'IngredientGroup', id: 'LIST' }
            ]
        }),

        deleteIngredientGroup: build.mutation<
            IngredientGroups.DeleteIngredientGroupResponse,
            { id: string }
        >({
            query: ({ id }) => ({
                url: `/ingredient-groups/${id}`,
                method: 'DELETE'
            }),
            // HTTP 204 — тела нет, парсить нечего (api-contracts.md).
            transformResponse: (): IngredientGroups.DeleteIngredientGroupResponse => ({}),
            invalidatesTags: (_result, _error, arg) => [
                { type: 'IngredientGroup', id: arg.id },
                { type: 'IngredientGroup', id: 'LIST' }
            ]
        }),

        reorderIngredientGroups: build.mutation<
            IngredientGroups.ReorderIngredientGroupsResponse,
            IngredientGroups.ReorderIngredientGroupsRequest
        >({
            query: (body) => ({
                url: '/ingredient-groups/reorder',
                method: 'POST',
                body
            }),
            transformResponse: (raw: unknown): IngredientGroups.ReorderIngredientGroupsResponse =>
                parseWithSchema(IngredientGroups.ReorderIngredientGroupsResponseSchema, raw),
            invalidatesTags: [{ type: 'IngredientGroup', id: 'LIST' }]
        })
    })
});

export const {
    useListIngredientGroupsQuery,
    useCreateIngredientGroupMutation,
    useUpdateIngredientGroupMutation,
    useDeleteIngredientGroupMutation,
    useReorderIngredientGroupsMutation
} = ingredientGroupsApi;
